import { Button } from "@/components/ui/baseUi/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import { OutOfBandTransactionStatus } from "@modules/finances/boxPaymentAtCounter/entities/outOfBandTransactionType"
import { useOutOfBandTransactionActions } from "@modules/finances/boxPaymentAtCounter/hooks/useOutOfBandTransactionActions"
import { TransactionDetailsEntity } from "@modules/finances/entities/transactionDetailsEntity"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { LoaderCircle } from "lucide-react"
import { useState } from "react"

type TransactionOutOfBandPaymentProps = {
  transactionDetails: TransactionDetailsEntity
  className?: string
} & React.ComponentProps<"div">

type PaymentMethod =
  | OutOfBandTransactionStatus.CARD
  | OutOfBandTransactionStatus.CHECK
  | OutOfBandTransactionStatus.CASH

export function TransactionOutOfBandPayment({
  transactionDetails,
  className,
  ...props
}: TransactionOutOfBandPaymentProps) {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | undefined>(undefined)

  const { hideSidebar } = useSidebar()
  const { validateOutOfBandTransactionMutation } = useOutOfBandTransactionActions()

  const paymentMethodOptions = [
    { value: OutOfBandTransactionStatus.CARD, label: word("finances.transaction.outOfBandPayment.card") },
    { value: OutOfBandTransactionStatus.CHECK, label: word("finances.transaction.outOfBandPayment.check") },
    { value: OutOfBandTransactionStatus.CASH, label: word("finances.transaction.outOfBandPayment.cash") },
  ]

  const onValidateOutOfBandTransaction = async () => {
    if (!selectedPaymentMethod) {
      toast.error(word("finances.transaction.outOfBandPayment.paymentMethodMissing"), {
        classNames: errorToastClassName(),
      })
      return
    }
    if (transactionDetails.invoiceId && !validateOutOfBandTransactionMutation.isPending) {
      await validateOutOfBandTransactionMutation.mutateAsync(
        {
          invoiceId: transactionDetails.invoiceId,
          newStatus: selectedPaymentMethod,
        },
        {
          onSuccess: () => {
            toast.success(word("finances.transaction.outOfBandPayment.success"), {
              classNames: successToastClassName(),
            })

            hideSidebar()
          },
          onError: (error) => {
            toast.error(word("finances.transaction.outOfBandPayment.error"), {
              description: error.message,
              classNames: errorToastClassName(),
            })
          },
        },
      )
    }
  }

  if (!transactionDetails.invoiceId) return

  return (
    <div
      className={cn(
        "card banner mb-1 flex flex-col gap-3 bg-surface-background-primary-information-default! p-6!",
        className,
      )}
      {...props}
    >
      <Text fontStyle="BS" className="text-textDefault-body uppercase">
        {word("finances.transaction.outOfBandPayment.label")}
      </Text>

      <div className="flex flex-row items-center justify-between gap-2">
        <Select
          value={selectedPaymentMethod}
          onValueChange={(value) => setSelectedPaymentMethod(value as PaymentMethod)}
          items={paymentMethodOptions}
        >
          <SelectTrigger className="w-7/10">
            <SelectValue placeholder={word("finances.transaction.outOfBandPayment.placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {paymentMethodOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="primary"
          size="lg"
          onClick={() => void onValidateOutOfBandTransaction()}
          className="grow"
          isLoading={validateOutOfBandTransactionMutation.isPending}
        >
          {validateOutOfBandTransactionMutation.isPending && <LoaderCircle className="animate-spin" />}
          {word("global.check")}
        </Button>
      </div>
    </div>
  )
}
